import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import iso3311a2 from 'iso-3166-1-alpha-2';
import {
  Autocomplete,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Button,
  Skeleton,
} from '@mui/material';
import PlanPaymentMethodCardBox from 'components/PlanPaymentMethodCardBox';
import { Icons } from 'components/Icons';
import Image from 'next/image';
import WhatsappPhoneTextField from 'components/WhatsappPhoneTextField';
import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js';
import { getCode, getCountries } from 'utils/countries';
import StripeElement from '../StripleElement';
import { PaymentMethodEnum, PlanEnum } from 'enums';
import { useCheckoutContext } from 'contexts/CheckoutContext';

const COLOR_MANDATORY = '#F01C10';

const useStyles = makeStyles((theme) => ({
  addCardButton: {
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(1),
  },
  colorMandatory: {
    color: COLOR_MANDATORY,
  },
  cardGrid: {
    width: '100% !important',
    marginLeft: 0,
  },
  inputCardNumber: {
    position: 'relative',
    marginTop: theme.spacing(3),

    '& input,fieldset': {
      borderColor: '#616161 !important',
      color: '#616161 !important',
    },

    '& span': {
      paddingLeft: theme.spacing(0.5),
      color: '#7C7C7C',
      fontSize: 12,
      position: 'absolute',
      top: -6,
      left: 12,
      zIndex: 1,
      backgroundColor: theme.palette.common.white,
    },
    '& span.highlight': {
      color: COLOR_MANDATORY,
      marginLeft: 112,
      paddingLeft: 0,
      paddingRight: 5,
    },
    '& span.highlightDate': {
      color: COLOR_MANDATORY,
      marginLeft: 65,
      paddingLeft: 1,
      paddingRight: 4,
    },
    '& span.highlightCVC': {
      color: COLOR_MANDATORY,
      marginLeft: 29,
      paddingLeft: 1,
      paddingRight: 5,
    },

    [theme.breakpoints.down('lg')]: {
      minWidth: '100%',
    },
  },
  stripeInput: {
    '& input,fieldset': {
      borderColor: '#616161 !important',
      color: '#616161 !important',
    },
  },
  cardsBanner: {
    display: 'flex',
    gap: '8px',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(4),
    position: 'relative',
  },
  divider: {
    width: '100%',
    marginBottom: theme.spacing(4),
  },
  whatsAppFieldAb: {
    width: '100%',

    '& p': {
      display: 'none !important',
    },

    [theme.breakpoints.down('lg')]: {
      maxWidth: '99%',
      marginLeft: '-3px',
    },
  },
  removePaddingLeft: {
    paddingLeft: '0 !important',
  },
}));

const initialFormData = {
  fields: {
    card_number: { value: '', required: true },
    card_expiry: { value: '', required: true },
    card_cvc: { value: '', required: true },
    name: { value: '', required: true },
    email: { value: '' },
    address: { value: '' },
    city: { value: '' },
    state: { value: '' },
    postal_code: { value: '' },
    country: { value: '', required: true },
    whatsappPhoneNumber: { value: '' },
  },
};

export const PaymentMethodListStatusEnum = {
  IDLE: 'IDLE',
  TRIGGERED: 'TRIGGERED',
  LOADED: 'LOADED',
  ERROR: 'ERROR',
};

export default function CreditCardContent() {
  const classes = useStyles();
  const { data, actions } = useCheckoutContext();
  const { stripe, stripeElements, paymentService, isMobile } = data;

  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState('');
  const [paymentMethodList, setPaymentMethodList] = useState({ data: [] });
  const [paymentMethodListStatus, setPaymentMethodListStatus] = useState(
    PaymentMethodListStatusEnum.IDLE,
  );
  const [showPaymentMethodForm, setShowPaymentMethodForm] = useState(false);
  const [formFieldsWithError, setFormFieldsWithError] = useState([]);
  const [formData, setFormData] = useState({
    ...initialFormData,
    fields: {
      ...initialFormData.fields,
      name: { value: data.user.name || '', required: true },
      email: { value: data.user.email || '' },
      country: {
        value: data.user.countryCode ? iso3311a2.getCountry(data.user.countryCode) : '',
        required: true,
      },
    },
  });

  const attachDefaultPaymentMethod = async (id) => {
    await paymentService.attachStripePaymentMethod({
      paymentMethodId: id,
    });
  };

  const getPaymentMethodId = async () => {
    if (defaultPaymentMethodId) {
      return { id: defaultPaymentMethodId };
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: stripeElements.getElement(CardNumberElement),
      billing_details: {
        name: formData.fields.name.value,
        address: {
          line1: formData.fields.address.value,
          city: formData.fields.city.value,
          state: formData.fields.state.value,
          postal_code: formData.fields.postal_code.value,
          country: getCode(formData.fields.country.value),
        },
      },
    });

    if (error) {
      throw new PaymentError(error.message);
    }

    return paymentMethod;
  };

  const handlePaymentMethodChange = (event) => {
    setDefaultPaymentMethodId(event.target.value);
    attachDefaultPaymentMethod(event.target.value);
  };

  const handleAddCardPaymentMethod = () => {
    setShowPaymentMethodForm(true);
    setDefaultPaymentMethodId('');
  };

  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      fields: {
        ...formData.fields,
        [field]: { value, required: formData.fields[field].required },
      },
    });

    if (formFieldsWithError.includes(field)) {
      setFormFieldsWithError(formFieldsWithError.filter((item) => item !== field));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const isFormValid = validate();

    if (!isFormValid) {
      return;
    }

    actions.beforeSubmit(formData);

    try {
      const paymentMethod = await getPaymentMethodId();
      const response = await paymentService.stripe({
        plan: data.plan,
        billingInterval: data.billingInterval,
        teamSize: data.plan === PlanEnum.LAB_RESEARCHER ? data.teamSize : undefined,
        paymentMethodId: paymentMethod.id,
        paymentMethodType: PaymentMethodEnum.CARD,
        couponCode: data.coupon?.code,
      });

      actions.afterSubmit({ success: true, data: response.data, paymentMethod });
    } catch (e) {
      actions.afterSubmit({ success: false, error: e });
    }
  };

  const validate = () => {
    const isUsingDefaultPaymentMethod = !showPaymentMethodForm && defaultPaymentMethodId;

    if (isUsingDefaultPaymentMethod) {
      return true;
    }

    const fields = Object.keys(formData.fields);
    const errorFields = [];

    for (const field of fields) {
      if (formData.fields[field].required && !formData.fields[field].value) {
        errorFields.push(field);
      }
    }

    if (errorFields.length > 0) {
      setFormFieldsWithError(errorFields);
      return false;
    } else {
      setFormFieldsWithError([]);
      return true;
    }
  };

  useEffect(() => {
    async function fetchStripePaymentMethods() {
      try {
        const paymentMethodList = await paymentService.getStripePaymentMethods();
        setPaymentMethodList(paymentMethodList);
        setPaymentMethodListStatus(PaymentMethodListStatusEnum.LOADED);
      } catch (e) {
        setPaymentMethodListStatus(PaymentMethodListStatusEnum.ERROR);
        console.error(e);
      }
    }

    if (data.user.ready && paymentMethodListStatus === PaymentMethodListStatusEnum.IDLE) {
      setPaymentMethodListStatus(PaymentMethodListStatusEnum.TRIGGERED);
      fetchStripePaymentMethods();
    }
  }, [data.user]);

  useEffect(() => {
    if (paymentMethodList.data.length > 0) {
      const firstPaymentMethod = paymentMethodList.data[0];
      const defaultPaymentMethod = paymentMethodList.data.find((item) => item.default);
      setDefaultPaymentMethodId(defaultPaymentMethod?.id ?? firstPaymentMethod?.id);

      if (firstPaymentMethod?.billing_details) {
        const billingDetails = firstPaymentMethod.billing_details;
        setFormData({
          ...formData,
          fields: {
            ...formData.fields,
            name: { value: billingDetails.name || '', error: '', required: true },
            address: { value: billingDetails.address?.line1 || '', error: '' },
            city: { value: billingDetails.address?.city || '', error: '' },
            state: { value: billingDetails.address?.state || '', error: '' },
            postalCode: {
              value: billingDetails.address?.postal_code || '',
              error: '',
            },
            country: {
              value: billingDetails.address?.country
                ? iso3311a2.getCountry(firstPaymentMethod.billing_details.address.country)
                : '',
              error: '',
              required: true,
            },
          },
        });
        setShowPaymentMethodForm(false);
      }
    } else if (paymentMethodListStatus === PaymentMethodListStatusEnum.LOADED) {
      setShowPaymentMethodForm(true);
    }
  }, [paymentMethodList]);

  useEffect(() => {
    if (data.profile?.whatsappNumber) {
      handleChange('whatsappPhoneNumber', data.profile.whatsappNumber);
    }
  }, [data.profile?.whatsappNumber]);

  useEffect(() => {
    if (formFieldsWithError.length > 0 && !data.hasFormError) {
      actions.setHasFormError(true);
    } else if (formFieldsWithError.length === 0 && data.hasFormError) {
      actions.setHasFormError(false);
    }
  }, [formFieldsWithError]);

  if (
    paymentMethodListStatus === PaymentMethodListStatusEnum.TRIGGERED &&
    !paymentMethodList.data.length
  ) {
    return (
      <Box>
        <Box mt={2} mb={4}>
          <Divider />
        </Box>
        <Skeleton variant="rectangular" width={462} height={78} />
      </Box>
    );
  }

  return (
    <div>
      {!showPaymentMethodForm ? (
        <form onSubmit={handleSubmit}>
          <Box mt={2} mb={4}>
            <Divider />
          </Box>

          <FormControl variant="standard">
            <RadioGroup
              name="paymentMethod"
              value={defaultPaymentMethodId}
              onChange={handlePaymentMethodChange}
            >
              <Grid container={true} spacing={2}>
                {paymentMethodList?.data?.length > 0 &&
                  paymentMethodList.data.map((item, index) => (
                    <Grid item={true} xs={12} sm={12} key={index}>
                      <FormControlLabel
                        label={
                          <PlanPaymentMethodCardBox
                            item={item}
                            isDefault={item.id === defaultPaymentMethodId}
                            isExpired={item.expired}
                          />
                        }
                        value={item.id}
                        control={<Radio color="secondary" disabled={item.expired} />}
                      />
                    </Grid>
                  ))}
              </Grid>
            </RadioGroup>
          </FormControl>

          <div>
            <Button
              className={classes.addCardButton}
              color="primary"
              startIcon={<Icons.addCircle />}
              onClick={handleAddCardPaymentMethod}
            >
              Add new card
            </Button>
          </div>

          <input type="submit" style={{ display: 'none' }} ref={data.formRef} />
        </form>
      ) : (
        <form onSubmit={handleSubmit}>
          <Grid item={true} xs={12} sm={12}>
            <FormControl variant="standard" fullWidth={true} margin="dense">
              <TextField
                variant="outlined"
                error={formFieldsWithError.includes('name')}
                helperText={
                  formFieldsWithError.includes('name') && 'Please fill this field correctly'
                }
                label={
                  <>
                    Name<span className={classes.colorMandatory}>*</span>
                  </>
                }
                placeholder=""
                InputLabelProps={{
                  shrink: true,
                }}
                name="name"
                value={formData.fields.name.value}
                onChange={(e) => handleChange('name', e.target.value)}
              />
            </FormControl>
          </Grid>
          <Grid container={true} spacing={1} className={classes.cardGrid}>
            <Grid
              item={true}
              xs={8}
              md={6}
              className={classes.removePaddingLeft}
              style={{ minWidth: isMobile && '100%', paddingLeft: '0 !important' }}
            >
              <div className={classes.inputCardNumber}>
                <span>Credit Card Number</span>
                <span className="highlight">*</span>
                <StripeElement
                  className={classes.stripeInput}
                  element={CardNumberElement}
                  error={formFieldsWithError.includes('card_number')}
                  onChange={(e) => handleChange('card_number', e.complete)}
                />
              </div>

              {formFieldsWithError.includes('card_number') && (
                <FormHelperText variant="outlined" error={true}>
                  Please fill this field correctly
                </FormHelperText>
              )}
            </Grid>

            <Grid item={true} xs={12} md={3}>
              <div className={classes.inputCardNumber}>
                <span>Expire date</span>
                <span className="highlightDate">*</span>
                <StripeElement
                  element={CardExpiryElement}
                  error={formFieldsWithError.includes('card_expiry')}
                  onChange={(e) => handleChange('card_expiry', e.complete)}
                />
              </div>

              {formFieldsWithError.includes('card_expiry') && (
                <FormHelperText variant="outlined" error={true}>
                  Please fill this field correctly
                </FormHelperText>
              )}
            </Grid>

            <Grid item={true} xs={12} md={3}>
              <div className={classes.inputCardNumber}>
                <span>CVV</span>
                <span className="highlightCVC">*</span>
                <StripeElement
                  element={CardCvcElement}
                  error={formFieldsWithError.includes('card_cvc')}
                  onChange={(e) => handleChange('card_cvc', e.complete)}
                />
              </div>

              {formFieldsWithError.includes('card_cvc') && (
                <FormHelperText variant="outlined" error={true}>
                  Please fill this field correctly
                </FormHelperText>
              )}
            </Grid>

            <div className={classes.cardsBanner}>
              <Image
                src="/images/visa-logo.svg"
                width={48}
                height={32}
                alt="Visa Logo"
                loading="lazy"
              />

              <Image
                src="/images/master-card-logo.svg"
                width={48}
                height={32}
                alt="Master Card Logo"
                loading="lazy"
              />

              <Image
                src="/images/american-express-logo.svg"
                width={48}
                height={32}
                alt="American Express Logo"
                loading="lazy"
              />

              <Image
                src="/images/discover-logo.svg"
                width={48}
                height={32}
                alt="Discover Logo"
                loading="lazy"
              />
            </div>

            <Divider color="#bdbdbd" className={classes.divider} />

            <Grid item={true} xs={6} sm={6} className={classes.removePaddingLeft}>
              <FormControl variant="standard" fullWidth={true} margin="dense">
                <Autocomplete
                  options={['', ...getCountries()]}
                  value={
                    formData.fields.country.value === 'Taiwan, Province of China'
                      ? 'Taiwan'
                      : formData.fields.country.value
                  }
                  onChange={(_, value) => handleChange('country', value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      error={formFieldsWithError.includes('country')}
                      helperText={
                        formFieldsWithError.includes('country') &&
                        'Please fill this field correctly'
                      }
                      label={
                        <>
                          Country<span className={classes.colorMandatory}>*</span>
                        </>
                      }
                      placeholder=""
                      InputLabelProps={{
                        shrink: true,
                      }}
                      name="country"
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item={true} xs={6} sm={6}>
              <FormControl variant="standard" fullWidth={true} margin="dense">
                <TextField
                  variant="outlined"
                  error={formFieldsWithError.includes('postal_code')}
                  helperText={
                    formFieldsWithError.includes('postal_code') &&
                    'Please fill this field correctly'
                  }
                  label="Postal Code"
                  placeholder=""
                  InputLabelProps={{
                    shrink: true,
                  }}
                  name="postal_code"
                  value={formData.fields.postal_code.value}
                  onChange={(e) => handleChange('postal_code', e.target?.value)}
                />
              </FormControl>
            </Grid>

            <Box
              pt={2}
              pb={data.errorMessage ? 0 : isMobile ? 1 : 6}
              className={classes.whatsAppFieldAb}
            >
              <WhatsappPhoneTextField
                variant="outlined"
                label="Phone Number (Optional)"
                onChange={(e) => handleChange('whatsappPhoneNumber', e.target?.value)}
                value={formData.fields.whatsappPhoneNumber.value}
                error={formFieldsWithError.includes('whatsappPhoneNumber')}
                helperText={
                  formFieldsWithError.includes('whatsappPhoneNumber') &&
                  'Please fill this field correctly'
                }
                marginRightProp={0}
              />
            </Box>
          </Grid>

          <input type="submit" style={{ display: 'none' }} ref={data.formRef} />
        </form>
      )}
    </div>
  );
}
